import React, { PropsWithChildren, useEffect, useState } from 'react';
import { BasicCheckbox } from '../basic-inputs';

export type FooterProps = PropsWithChildren<{
  pagination?: React.ReactElement<PaginationProps>;
  multiSelectCount?: number;
  isDetailPaneOpen?: boolean;
}>;

export const DatagridFooter = React.forwardRef<HTMLDivElement, FooterProps>(
  (props, ref) => {
    const { children, pagination, multiSelectCount, isDetailPaneOpen } = props;
    return (
      <div ref={ref} className="datagrid-footer">
        {multiSelectCount && multiSelectCount > 0 ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
            <BasicCheckbox
              label={multiSelectCount.toString()}
              disabled={true}
              checked={true}
            />
          </div>
        ) : undefined}
        {!isDetailPaneOpen && (
          <div className="datagrid-footer-description" role="status">
            {children}
          </div>
        )}
        {pagination}
      </div>
    );
  }
);

export type PaginationProps = PropsWithChildren<{
  pageSize: number;
  currentPage?: number;
  totalRows: number;
  onPreviousPage?: () => void;
  onNextPage?: () => void;
  onFirstPage?: () => void;
  onLastPage?: (page: number) => void;
  onPage?: (page: number) => void;
  isDetailPaneOpen?: boolean;
}>;

export const DatagridPagination = React.forwardRef<
  HTMLDivElement,
  PaginationProps
>((props, ref) => {
  const {
    children,
    currentPage,
    pageSize,
    totalRows,
    onFirstPage,
    onLastPage,
    onNextPage,
    onPage,
    onPreviousPage,
    isDetailPaneOpen
  } = props;
  const lastPage = Math.ceil(totalRows / pageSize) || 1;
  const [page, setPage] = useState(currentPage!.toString());

  useEffect(() => {
    setPage(currentPage!.toString());
  }, [currentPage]);

  useEffect(() => {
    if (lastPage < currentPage!) {
      setPage(lastPage.toString());
      onPage!(lastPage);
    }
  }, [currentPage, lastPage, onPage]);

  const handlePageChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setPage(e.target.value);

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      let value = Number.isNaN(+page) ? currentPage! : +page;
      value = value > lastPage ? lastPage : value;
      setPage(value.toString());
      onPage!(value);
    }
  };

  const renderCompactFooter = () => {
    const startIndex = (currentPage! - 1) * pageSize;
    const lastIndex = Math.min(startIndex + pageSize, totalRows);
    return (
      <>
        <div className="pagination-description-compact">
          {startIndex + 1}-{lastIndex} / {totalRows}
        </div>
        <div className="pagination-list">
          <button
            className="pagination-previous"
            disabled={+page === 1}
            onClick={onPreviousPage}
            aria-label="Previous Page">
            <clr-icon shape="angle left" />
          </button>
          <input
            className="pagination-current clr-input"
            value={page}
            type="text"
            size={2}
            onKeyPress={handleKeyPress}
            onChange={handlePageChange}
            aria-label="Current Page"
          />
          <button
            className="pagination-next"
            disabled={+page === lastPage}
            onClick={onNextPage}
            aria-label="Next Page">
            <clr-icon shape="angle right" />
          </button>
        </div>
      </>
    );
  };

  const handleLastPage = () => onLastPage!(lastPage);
  return (
    <div ref={ref} className="pagination">
      {isDetailPaneOpen ? (
        renderCompactFooter()
      ) : (
        <>
          <div className="pagination-description">{children}</div>
          <div className="pagination-list">
            <button
              className="pagination-first"
              disabled={+page === 1}
              onClick={onFirstPage}
              aria-label="First Page">
              <clr-icon shape="step-forward-2 down" />
            </button>
            <button
              className="pagination-previous"
              disabled={+page === 1}
              onClick={onPreviousPage}
              aria-label="Previous Page">
              <clr-icon shape="angle left" />
            </button>
            <input
              className="pagination-current clr-input"
              value={page}
              type="text"
              size={2}
              onKeyPress={handleKeyPress}
              onChange={handlePageChange}
              aria-label="Current Page"
            />
            &nbsp;/&nbsp;
            <span aria-label="Total Pages">{lastPage}</span>
            <button
              className="pagination-next"
              disabled={+page === lastPage}
              onClick={onNextPage}
              aria-label="Next Page">
              <clr-icon shape="angle right" />
            </button>
            <button
              className="pagination-last"
              disabled={+page === lastPage}
              onClick={handleLastPage}
              aria-label="Last Page">
              <clr-icon shape="step-forward-2 up" />
            </button>
          </div>
        </>
      )}
    </div>
  );
});

export type PageSizeProps = PropsWithChildren<{
  pageSize: number;
  pageSizeOptions: number[];
  onPageSizeChange: (pageSize: number) => void;
}>;

export const DatagridPageSize: React.FC<PageSizeProps> = React.forwardRef<
  HTMLDivElement,
  PageSizeProps
>((props, ref) => {
  const { children, pageSize, pageSizeOptions, onPageSizeChange } = props;

  const onChangePageSize = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onPageSizeChange(Number(event.target.value));
  };

  return (
    <div ref={ref}>
      {children}
      <div className="clr-select-wrapper">
        <select
          className="clr-page-size-select"
          value={pageSize}
          onChange={onChangePageSize}>
          {pageSizeOptions.map(o => (
            <option value={o}>{o}</option>
          ))}
        </select>
      </div>
    </div>
  );
});
